import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import wlBgVideo from '../assets/preview.mp4';

// Define the structure for an NFT item
interface NftItem {
  id: number;
  imageUrl: string;
  altText: string;
}

// Get all character images dynamically
const characterImages = import.meta.glob('/src/assets/characters/*.png', { eager: true, import: 'default' });
const characterImagePaths = Object.keys(characterImages);

// --- Main Component ---
export function PurrtardioPreview() {
  // --- State ---
  // State to hold the NFT data. Initialize with an empty array.
  const [nfts, setNfts] = useState<NftItem[]>([]);
  // State to track which row is being interacted with
  const [activeRows, setActiveRows] = useState<{[key: string]: boolean}>({
    row1: false,
    row2: false,
    row3: false
  });
  // Define the number of unique NFTs to generate
  const numberOfNfts = 100;
  // Total number of NFTs in the collection
  const totalNftsInCollection = 6789;

  // --- Effects ---
  // useEffect hook to generate NFT data when the component mounts
  useEffect(() => {
    // Generate an array of random IDs from the collection range (1-6789)
    const randomNftIds: number[] = [];
    while (randomNftIds.length < numberOfNfts) {
      const randomId = Math.floor(Math.random() * totalNftsInCollection) + 1;
      if (!randomNftIds.includes(randomId)) {
        randomNftIds.push(randomId);
      }
    }

    // Create the NFT items using the random IDs
    const generatedNfts: NftItem[] = randomNftIds.map(id => ({
      id,
      imageUrl: `https://ipfs-art.purrtardio.com/${id}.jpg`,
      altText: `Purrtardio NFT #${id}`,
    }));

    setNfts(generatedNfts);
  }, []); // Empty dependency array ensures this runs only once on mount



  // --- Helper Function ---
  // Function to render a single slideshow row
  const renderSlideshowRow = (items: NftItem[], direction: string, keyPrefix: string) => {
    const isActive = activeRows[keyPrefix];
    const animationClass = `${direction} ${isActive ? 'active' : 'inactive'} will-change-transform`;
    
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (characterImagePaths.length > 0) {
        const randomIndex = Math.floor(Math.random() * characterImagePaths.length);
        const randomCharacterPath = characterImagePaths[randomIndex];
        const fallbackSrc = characterImages[randomCharacterPath] as string;
        console.log('Image error, attempting fallback src:', fallbackSrc);
        // Vite's import.meta.glob provides paths relative to the root, 
        // but the 'default' import gives the resolved path/URL for the asset.
        e.currentTarget.src = fallbackSrc;
      }
      // Optionally, add a fallback if even the character images fail, though unlikely
      // e.currentTarget.src = 'some_ultimate_fallback.png'; 
    };
    
    return (
      // The outer div clips the content that overflows
      <div 
        className="w-full overflow-hidden rounded-lg mb-4 cursor-pointer"        
      >
        {/*
          * The inner div contains the items and applies the animation.
          * 'w-max' ensures the container is wide enough for all items.
          * 'flex' lays out the items horizontally.
          * 'animate-...' applies the custom CSS animation.
        */}
        <div className={`w-max flex ${animationClass}`}>
          {/* Render each item once for a unique display */}
          {items.map((nft, index) => (
            <div key={`${keyPrefix}-${nft.id}-${index}`} className="flex-shrink-0 w-48 h-48 mr-4 rounded-lg overflow-hidden shadow-lg bg-gray-800">
              <img
                src={nft.imageUrl}
                alt={nft.altText}
                className="w-full h-full object-cover"
                // Use the new error handler
                onError={handleImageError}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // --- Render ---
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          className="video-background"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src={wlBgVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Overlay to ensure text is readable */}
        <div className="video-overlay"></div>
      </div>

      {/* Inject custom CSS animations using a style tag */}
      <style>
        {`
          @keyframes slide-right-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(calc(-100% + 100vw)); }
          }
          @keyframes slide-left-right {
            0% { transform: translateX(calc(-100% + 100vw)); }
            100% { transform: translateX(0); }
          }
          .slide-right-left.active {
            animation: slide-right-left 15s linear infinite;
          }
          .slide-right-left.inactive {
            animation: slide-right-left 90s linear infinite;
          }
          .slide-left-right.active {
            animation: slide-left-right 15s linear infinite;
          }
          .slide-left-right.inactive {
            animation: slide-left-right 90s linear infinite;
          }
          /* Ensure smooth animations */
          .will-change-transform {
             will-change: transform;
          }
        `}
      </style>

      {/* Main container with Tailwind classes */}
      <div className="relative z-10 p-8 flex flex-col items-center">

           {/* Call to Action Section */}
        <motion.section 
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <a href="https://kingdom.purrtardio.com/ranks">
           <motion.button 
             className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-2xl font-bold rounded-lg shadow-lg hover:from-purple-700 hover:to-pink-700 transform transition-all duration-300 border-2 border-white/20 backdrop-blur-sm"
             whileHover={{ scale: 1.05 }}
             whileTap={{ scale: 0.98 }}
           >
             Explore Purrtardios
           </motion.button>
          </a>
        </motion.section>
        

        {/* NFT Slideshow Section */}
        <motion.section 
          className="w-full max-w-7xl flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >          

          {/* Render the three slideshow rows if NFTs are loaded */}
          {nfts.length > 0 ? (
            <>
              {/* Row 1: Right to Left */}
              {renderSlideshowRow(nfts.slice(0, numberOfNfts / 3), 'slide-right-left', 'row1')}
              {/* Row 2: Left to Right */}
              {renderSlideshowRow(nfts.slice(numberOfNfts / 3, 2 * numberOfNfts / 3), 'slide-left-right', 'row2')}
              {/* Row 3: Right to Left */}
              {renderSlideshowRow(nfts.slice(2 * numberOfNfts / 3), 'slide-right-left', 'row3')}
            </>
          ) : (
            // Show loading indicator while NFTs are generated
            <div className="flex flex-col items-center">
              <p className="text-xl mb-2">Loading preview...</p>
              {/* Loading animation */}
              <div className="flex space-x-2 mt-2">
                <motion.div
                  className="w-3 h-3 bg-purple-500 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatType: "loop" }}
                />
                <motion.div
                  className="w-3 h-3 bg-purple-500 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatType: "loop", delay: 0.2 }}
                />
                <motion.div
                  className="w-3 h-3 bg-purple-500 rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatType: "loop", delay: 0.4 }}
                />
              </div>
            </div>
          )}
        </motion.section>
      </div>
    </div>
  );
} 